import {
  usePausePiiBackfill,
  useStartPiiBackfill,
} from "@/api/pii-vault/use-pii-backfill";
import {
  DEFAULT_ORDER_WINDOW,
  DEFAULT_PARALLELISM,
  OrderWindowSelect,
  orderMonthsOf,
  ParallelismInput,
} from "@/components/pii-backfill/backfill-settings";
import type { PiiBackfillStateDto } from "@babackapp/private-api-client/generated/schema";
import { Button } from "@babackapp/ui/components/button";
import { subMonths } from "date-fns";
import { Loader2, Pause, Play } from "lucide-react";
import { useState } from "react";

const COLLECTION_COUNT = 3;

type StartAction = "start" | "resume" | "widen" | "restart";

const START_LABELS: Record<StartAction, string> = {
  start: "Lancer",
  resume: "Reprendre",
  widen: "Continuer",
  restart: "Refaire",
};

/**
 * A finished shop is widened when the chosen window reaches older orders than
 * the run covered, and redone from scratch otherwise.
 */
export const startActionOf = (
  states: PiiBackfillStateDto[],
  orderMonths: number,
): StartAction => {
  if (states.length === 0) return "start";
  const isComplete =
    states.length === COLLECTION_COUNT && states.every((state) => state.isDone);
  if (!isComplete) return "resume";
  const coveredSince = states.find((s) => s.collection === "order")?.since;
  const requestedSince = orderMonths
    ? subMonths(new Date(), orderMonths).getTime()
    : null;
  const isWider =
    coveredSince != null &&
    (requestedSince === null || requestedSince < coveredSince);
  return isWider ? "widen" : "restart";
};

export function PiiBackfillControls({
  shopId,
  states,
}: {
  shopId: string;
  states: PiiBackfillStateDto[];
}) {
  const start = useStartPiiBackfill(shopId);
  const pause = usePausePiiBackfill(shopId);
  const [parallelism, setParallelism] = useState(DEFAULT_PARALLELISM);
  const [orderWindow, setOrderWindow] = useState(DEFAULT_ORDER_WINDOW);

  const orderMonths = orderMonthsOf(orderWindow);
  const action = startActionOf(states, orderMonths);
  const isRunning = states.some((state) => !state.isDone && !state.isPaused);
  const isPending = start.isPending || pause.isPending;

  return (
    <div className="flex items-center gap-2 shrink-0">
      <OrderWindowSelect value={orderWindow} onChange={setOrderWindow} />
      <ParallelismInput value={parallelism} onChange={setParallelism} />
      <Button
        size="sm"
        disabled={isRunning || isPending}
        onClick={() =>
          start.mutate({
            params: { path: { shopId } },
            body: {
              parallelism,
              orderMonths,
              isRestart: action === "restart",
            },
          })
        }
      >
        {isPending ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <Play className="w-4 h-4" />
        )}
        {START_LABELS[action]}
      </Button>
      <Button
        size="sm"
        variant="secondary"
        disabled={!isRunning || isPending}
        onClick={() => pause.mutate({ params: { path: { shopId } } })}
      >
        <Pause className="w-4 h-4" />
        Pause
      </Button>
    </div>
  );
}
